/* @jsx h */
import { animate, createSpring } from "animejs";
import { defineComponent, h, onBeforeUnmount, onMounted, ref } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";
import { cn } from "../utils";

export type PageComponentProps = {
  title: string;
  titleIcon: string;
  subpages: { name: string; icon: string; href: string }[];
};

const getLinkClass = (isActive?: boolean) => {
  return cn(
    "grid grid-cols-[auto_1fr] items-center gap-2 cursor-pointer border-l-4 py-2 px-4 transition-all duration-300",
    isActive
      ? "border-l-blue-500 font-semibold "
      : "border-l-transparent hover:text-accent",
  );
};

const COLLAPSED_WIDTH = 64;
const EXPANDED_WIDTH = 256;
const REVEAL_DISTANCE = EXPANDED_WIDTH - COLLAPSED_WIDTH;

const SIDEBAR_STORAGE_KEY = "lemmon-sidebar";

// The toggle means opposite things on either side of 2xl (there it overrides
// an expanded default, below it pins open a collapsed one), so both halves are
// stored and syncWithViewport picks whichever applies.
type StoredSidebarState = {
  wideCollapsed: boolean;
  narrowExpanded: boolean;
};

const readStoredSidebarState = (): StoredSidebarState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSidebarState>;
    return {
      wideCollapsed: parsed.wideCollapsed === true,
      narrowExpanded: parsed.narrowExpanded === true,
    };
  } catch {
    // Storage disabled or a corrupt entry: fall back to the defaults.
    return null;
  }
};

const writeStoredSidebarState = (state: StoredSidebarState) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage disabled: the sidebar just won't remember its state.
  }
};

// The sidebar itself never animates: it always renders its full width, icons
// and labels included. It sits underneath the main content, which normally
// covers everything past the icon rail. Expanding just slides the content
// aside (translateX) to reveal the labels that were sitting under it.
//
// Whether that slide also resizes the content depends on how the sidebar was
// opened, not on the breakpoint:
//
// A committed open -- the 2xl default, the toggle button, the state a drag
// lands on -- gives the sidebar a real gutter. The pane is `flex-1` next to a
// `ml-16` rail, so its box is `container - 64`; sliding it right by
// REVEAL_DISTANCE alone would push its right edge that far past the wrapper,
// which is `overflow-hidden` -- the content just gets cut off. An equal
// margin-right shrinks the box so the right edge stays pinned and the content
// lays out in the width that is actually left.
//
// A hover peek is transient, so it skips the margin: the pane slides rigidly,
// keeps its width, and never reflows. The overhang is clipped by the wrapper
// for as long as the pointer stays on the sidebar. A drag is transient the
// same way while the finger is down, and settles into a committed open or
// close on release.
export const SideMenu = defineComponent<PageComponentProps>({
  name: "ReportsMenu",
  props: ["title", "titleIcon", "subpages"],
  setup(props) {
    const route = useRoute();
    const sidebarRef = ref<HTMLElement | null>(null);
    const contentRef = ref<HTMLElement | null>(null);
    const isExpanded = ref(false);
    const isDragging = ref(false);
    const dragStartX = ref(0);
    const dragStartY = ref(0);
    const dragStartReveal = ref(0);
    const didDrag = ref(false);
    const dragGestureQualified = ref(false);
    // 2xl is expanded by default; the toggle button can override that.
    // Both of these are restored from localStorage on mount.
    const isWideCollapsedOverride = ref(false);
    // Below 2xl the sidebar starts collapsed; the toggle button (or a
    // completed drag) can pin it open until the user collapses it again.
    const isNarrowPinnedExpanded = ref(false);
    const persistSidebarState = () =>
      writeStoredSidebarState({
        wideCollapsed: isWideCollapsedOverride.value,
        narrowExpanded: isNarrowPinnedExpanded.value,
      });
    let contentAnimation: ReturnType<typeof animate> | null = null;
    let labelAnimation: ReturnType<typeof animate> | null = null;
    let wasWideDesktop: boolean | null = null;

    const LG_QUERY = "(min-width: 1024px)";
    const WIDE_DESKTOP_QUERY = "(min-width: 1536px)";
    const DESKTOP_HOVER_QUERY = "(hover: hover) and (pointer: fine)";
    const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
    // Tuned to feel like the content curtain snapping into place: light
    // mass, fairly stiff, lightly underdamped so it settles with a small
    // bounce instead of a mechanical ease.
    const SPRING = createSpring({ mass: 1, stiffness: 300, damping: 22 });

    const matches = (query: string) =>
      typeof window !== "undefined" && window.matchMedia(query).matches;

    const isDesktopLayout = () => matches(LG_QUERY);
    const isWideDesktop = () => matches(WIDE_DESKTOP_QUERY);
    const supportsDesktopHover = () => matches(DESKTOP_HOVER_QUERY);

    const getLabels = () =>
      sidebarRef.value
        ? Array.from(
            sidebarRef.value.querySelectorAll<HTMLElement>(
              "[data-sidebar-label]",
            ),
          )
        : [];

    // Drag tracks the finger with the transform alone -- handleTouchStart
    // parks the margin at 0, so nothing reflows mid-gesture.
    const setDragReveal = (reveal: number) => {
      if (!contentRef.value) return;
      contentRef.value.style.transform = `translateX(${reveal}px)`;
      const progress = Math.max(0, Math.min(1, reveal / REVEAL_DISTANCE));
      getLabels().forEach((label) => {
        label.style.opacity = `${progress}`;
      });
    };

    type SetExpandedOptions = {
      animated?: boolean;
      // `false` for a hover peek: slide the pane without resizing it, so its
      // contents never reflow. See the note above.
      shrink?: boolean;
    };

    const setExpanded = (
      expanded: boolean,
      { animated = true, shrink = true }: SetExpandedOptions = {},
    ) => {
      const content = contentRef.value;
      if (!content) return;

      isExpanded.value = expanded;
      contentAnimation?.cancel();
      labelAnimation?.cancel();

      // Below lg the sidebar is `hidden`, so there is nothing to reveal --
      // offsetting the content there would just leave a gap on the left.
      const reveal = expanded && isDesktopLayout() ? REVEAL_DISTANCE : 0;
      const margin = shrink ? reveal : 0;
      const labels = getLabels();
      const reduceMotion = matches(REDUCED_MOTION_QUERY);

      if (!animated || reduceMotion) {
        content.style.transform = `translateX(${reveal}px)`;
        content.style.marginRight = `${margin}px`;
        labels.forEach((label) => {
          label.style.opacity = expanded ? "1" : "0";
        });
        return;
      }

      contentAnimation = animate(content, {
        translateX: reveal,
        marginRight: `${margin}px`,
        ease: SPRING,
      });
      labelAnimation = animate(labels, {
        opacity: expanded ? 1 : 0,
        duration: 180,
        delay: expanded ? 90 : 0,
        ease: "easeOutQuad",
      });
    };

    const syncWithViewport = () => {
      const wideDesktop = isWideDesktop();
      if (wideDesktop) {
        // Only on a real narrow -> wide crossing; on the first sync
        // (wasWideDesktop === null) the restored preference wins.
        if (wasWideDesktop === false) {
          isWideCollapsedOverride.value = false;
          persistSidebarState();
        }
        setExpanded(!isWideCollapsedOverride.value, { animated: false });
      } else {
        setExpanded(isNarrowPinnedExpanded.value, { animated: false });
      }
      wasWideDesktop = wideDesktop;
    };

    const toggleSidebar = () => {
      if (isWideDesktop()) {
        isWideCollapsedOverride.value = isExpanded.value;
        setExpanded(!isExpanded.value);
      } else {
        isNarrowPinnedExpanded.value = !isExpanded.value;
        setExpanded(isNarrowPinnedExpanded.value);
      }
      persistSidebarState();
    };

    const handlePointerEnter = () => {
      if (
        isDesktopLayout() &&
        !isWideDesktop() &&
        supportsDesktopHover() &&
        !isNarrowPinnedExpanded.value
      ) {
        setExpanded(true, { shrink: false });
      }
    };

    const handlePointerLeave = () => {
      if (
        isDesktopLayout() &&
        !isWideDesktop() &&
        supportsDesktopHover() &&
        !isDragging.value &&
        !isNarrowPinnedExpanded.value
      ) {
        setExpanded(false, { shrink: false });
      }
    };

    // Uses raw Touch events (not Pointer Events / setPointerCapture) to
    // match RightSheet's drag approach — pointer capture is unreliable
    // inside the Expo WebView the POS app runs in on tablets.
    const handleTouchStart = (event: TouchEvent) => {
      if (!isDesktopLayout() || isWideDesktop() || !contentRef.value) return;
      const touch = event.touches[0];
      if (!touch) return;
      contentAnimation?.cancel();
      // Drop the committed margin up front: the box widens back to the
      // collapsed size, but the extra is clipped on the right so nothing
      // visibly moves, and the rest of the gesture stays transform-only.
      contentRef.value.style.marginRight = "0px";
      isDragging.value = true;
      didDrag.value = false;
      dragGestureQualified.value = false;
      dragStartX.value = touch.clientX;
      dragStartY.value = touch.clientY;
      const matrix = new DOMMatrix(
        getComputedStyle(contentRef.value).transform,
      );
      dragStartReveal.value = matrix.m41;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isDragging.value || !contentRef.value) return;
      const touch = event.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - dragStartX.value;
      const deltaY = Math.abs(touch.clientY - dragStartY.value);

      if (!dragGestureQualified.value) {
        if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 10) {
          dragGestureQualified.value = true;
        } else {
          return; // let vertical scroll happen instead
        }
      }

      event.preventDefault();
      didDrag.value = true;
      const reveal = Math.max(
        0,
        Math.min(REVEAL_DISTANCE, dragStartReveal.value + deltaX),
      );
      setDragReveal(reveal);
    };

    const handleTouchEnd = () => {
      if (!isDragging.value || !contentRef.value) return;
      isDragging.value = false;
      if (!dragGestureQualified.value) return;
      dragGestureQualified.value = false;
      const matrix = new DOMMatrix(
        getComputedStyle(contentRef.value).transform,
      );
      const expand = matrix.m41 >= REVEAL_DISTANCE / 2;
      isNarrowPinnedExpanded.value = expand;
      setExpanded(expand);
      persistSidebarState();
    };

    const preventClickAfterDrag = (event: MouseEvent) => {
      if (!didDrag.value) return;
      event.preventDefault();
      event.stopPropagation();
      didDrag.value = false;
    };

    onMounted(() => {
      const stored = readStoredSidebarState();
      if (stored) {
        isWideCollapsedOverride.value = stored.wideCollapsed;
        isNarrowPinnedExpanded.value = stored.narrowExpanded;
      }
      syncWithViewport();
      sidebarRef.value?.addEventListener("click", preventClickAfterDrag, true);
      window.addEventListener("resize", syncWithViewport);
    });

    onBeforeUnmount(() => {
      sidebarRef.value?.removeEventListener(
        "click",
        preventClickAfterDrag,
        true,
      );
      window.removeEventListener("resize", syncWithViewport);
      contentAnimation?.cancel();
      labelAnimation?.cancel();
    });

    return () => {
      return (
        <div class="relative flex-1 flex h-full overflow-hidden">
          <div
            ref={sidebarRef}
            data-expanded={isExpanded.value || undefined}
            class="hidden lg:flex absolute inset-y-0 left-0 w-64 glossy-bg z-[0] flex-col print:hidden"
            style={{ touchAction: "pan-y" }}
            onPointerenter={handlePointerEnter}
            onPointerleave={handlePointerLeave}
            onTouchstart={handleTouchStart}
            onTouchmove={handleTouchMove}
            onTouchend={handleTouchEnd}
            onTouchcancel={handleTouchEnd}
          >
            <h2 class="shrink-0 grid grid-cols-[auto_1fr] items-center gap-3 px-4 py-4 mb-0 text-accent text-headline ">
              <span class="material-symbols-outlined text-2xl border rounded  w-8 h-8 flex items-center justify-center bg-base-300">
                {props.titleIcon}
              </span>
              <span data-sidebar-label class="text-2xl truncate opacity-0">
                {props.title}
              </span>
            </h2>
            <div class="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
              {props.subpages.map((subpage) => {
                const isActive = [subpage.href].includes(route.path);
                return (
                  <RouterLink
                    data-active={isActive || undefined}
                    to={subpage.href}
                    class={`group ${getLinkClass(isActive)} ${isActive ? "is-sidelink-active text-accent" : ""}`}
                  >
                    <span class="material-symbols-outlined text-2xl min-w-[24px]">
                      {subpage.icon}
                    </span>
                    <span data-sidebar-label class="truncate opacity-0">
                      {subpage.name}
                    </span>
                  </RouterLink>
                );
              })}
            </div>
            <button
              type="button"
              class="shrink-0 flex items-center gap-3 border-t border-base-content/10 px-4 py-3 text-base-content/60 transition-colors duration-200 hover:bg-base-200/60 hover:text-accent"
              aria-label={props.title}
              aria-expanded={isExpanded.value}
              title={props.title}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                toggleSidebar();
              }}
            >
              <span class="material-symbols-outlined text-2xl min-w-[24px]">
                {isExpanded.value ? "left_panel_close" : "left_panel_open"}
              </span>
            </button>
          </div>
          <div
            ref={contentRef}
            class="page-content relative flex-1 flex overflow-x-hidden px-0 border-b lg:border-l border-base-content/10 bg-base-100 z-[10] ml-0 lg:ml-16 print:border-0 print:overflow-visible print:bg-white print:ml-0"
          >
            <RouterView />
          </div>
        </div>
      );
    };
  },
});
//
export const PageComponent = defineComponent<PageComponentProps>({
  name: "PageComponent",
  props: ["title", "titleIcon", "subpages"],
  setup(props) {
    return () => {
      return (
        <div class="page-component flex h-full p-0  flex-1 z-[0] print:p-0 print:h-auto print:bg-white print:text-slate-900">
          <div class="page-wrapper flex flex-1 h-full relative overflow-hidden shadow shadow-2xl print:border-0 print:shadow-none print:overflow-visible print:bg-white ">
            <SideMenu {...props} />
          </div>
        </div>
      );
    };
  },
});
